import ArrowDownward from "@mui/icons-material/ArrowDownward";
import { Box, Collapse, Divider, Typography } from "@mui/material";
import type { FC, MouseEvent } from "react";
import { getRecommendationTitle, staffingStatusMap } from "@/components/utils/Recommendations-utils";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { ALERT_CORAL, FONT_SIZE, NEUTRAL, SEVERITY } from "@/theme/tokens";
import type { StaffingIssue } from "@/utils/autoStaffing";
import { IssuePriority } from "@/utils/models";

interface IProps {
	recommendations: StaffingIssue[];
	isRecommendationsExpanded: boolean;
	onExpandRecommendations: (event: MouseEvent) => void;
	onClick: () => void;
}

export const DayIssues: FC<IProps> = ({ recommendations, onExpandRecommendations, onClick, isRecommendationsExpanded }) => {
	const { hospitalData, isAutoScheduler, chosenDepartment } = useHospitalData();
	// An overtime issue names its nurse from the staff, which the provider holds only with the cache off.
	const { nurses } = useRosterLookup(chosenDepartment.department_id);

	const formatRecommendationTitle = (issue: StaffingIssue): string => {
		if (!issue) return "";

		const { subtitle } = getRecommendationTitle(issue, { weeks: hospitalData.weeks, nurses }, isAutoScheduler);
		if (!subtitle) return "";

		const firstTwoWords = subtitle.split(" ").slice(0, 2).join(" ");
		const title = firstTwoWords.charAt(0).toUpperCase() + firstTwoWords.slice(1).toLowerCase();
		return staffingStatusMap.get(title.toLowerCase()) || "--";
	};

	return (
		<>
			{recommendations.length > 0 ? (
				<Box
					sx={{
						cursor: "pointer",
						"&:hover": {
							bgcolor: NEUTRAL[50],
						},
						transition: "background-color 0.2s ease",
						px: 1,
						borderRadius: "4px",
					}}
				>
					{recommendations.slice(0, 1).map((issue) => (
						<Box
							key={issue.issueType}
							sx={{
								height: "28px",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Box onClick={onClick} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Box
									sx={{
										width: "10px",
										height: "10px",
										borderRadius: "50%",
										backgroundColor:
											issue.priority === IssuePriority.TopPriority
												? ALERT_CORAL
												: issue.priority === IssuePriority.Important
													? SEVERITY.MEDIUM
													: "primary.main",
										flexShrink: 0,
									}}
								/>
								<Typography sx={{ fontWeight: 400, fontSize: FONT_SIZE.EMPHASIS, color: "primary.dark" }}>{formatRecommendationTitle(issue)}</Typography>
							</Box>
							{recommendations.length > 1 && (
								<ArrowDownward
									onClick={onExpandRecommendations}
									className="arrow-icon"
									sx={{
										color: "primary.main",
										transform: isRecommendationsExpanded ? "rotate(180deg)" : "rotate(0deg)",
										transition: "transform 0.3s ease, opacity 0.2s ease",
										fontSize: 16,
									}}
								/>
							)}
						</Box>
					))}
					<Collapse in={isRecommendationsExpanded}>
						{recommendations.slice(1).map((issue, index) => (
							<Box key={`${issue.notificationId}-${index}`} sx={{ height: "28px" }}>
								<Divider />
								<Box onClick={onClick} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<Box
										sx={{
											width: "12px",
											height: "12px",
											borderRadius: "50%",
											backgroundColor:
												issue.priority === IssuePriority.TopPriority
													? ALERT_CORAL
													: issue.priority === IssuePriority.Important
														? SEVERITY.MEDIUM
														: "primary.main",
											flexShrink: 0,
										}}
									/>
									<Typography sx={{ fontWeight: 400, fontSize: FONT_SIZE.EMPHASIS, color: "primary.dark" }}>{formatRecommendationTitle(issue)}</Typography>
								</Box>
							</Box>
						))}
					</Collapse>
				</Box>
			) : (
				<Box
					sx={{
						cursor: "pointer",
						height: "28px",
						"&:hover": {
							bgcolor: NEUTRAL[50],
						},
						transition: "background-color 0.2s ease",
						px: 1,
						borderRadius: "4px",
					}}
				>
					<Typography sx={{ fontWeight: 400, fontSize: FONT_SIZE.EMPHASIS, color: "primary.dark" }}>--</Typography>
				</Box>
			)}
		</>
	);
};
